import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRecurly } from '@recurly/react-recurly';
import { ValidationManager, PaymentMethodsCommandNames, StatusTypes, sendRioEvent, parseString, SpecType, SubType, ElementType, VendorType, UserAddressCommandNames } from '@chegg/commerce-utils';
import { updateFormFieldData, updatePaymentState } from '../../../redux/PaymentState/PaymentState.actions';
import { CardTypes } from '../../../constants/PaymentTypes';
import useCreateRecurlyMop from '../../../hooks/useCreateRecurlyMop';
import { saveConfigs } from '../../../redux/ConfigState/ConfigState.actions';
import { configSelector } from '../../../redux/ConfigState/ConfigState.selectors';
import { doFireStatusUpdate, sendRioEventCallback } from '../../../utils/CallbackUtils';
import { Component } from '../../../constants/CallbackConstants';
import { ERRORS, InlineErrors } from '../../../constants/Errors';
import { mopOnFileSelector } from '../../../redux/MopOnFileState/MopOnFileState.selectors';
import { getString } from '../../../constants/Strings';
import { sendNewRelicMessage } from '../../../utils/NewRelicUtils';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { paymentSelector } from '../../../redux/PaymentState/PaymentState.selectors';
import { Zipcode, EMPTY_ADDRESS_VALUE } from '../../../constants/AppConstants';
import { saveUserAddressCommands } from '../../../redux/CommandState/CommandState.actions';
var _ValidationManager$ty = ValidationManager.types,
  CCFIELDS = _ValidationManager$ty.CCFIELDS,
  VALIDATION_TYPES = _ValidationManager$ty.VALIDATION_TYPES;

// Recurly does not export these types
export var RecurlyCreditCardBrand = /*#__PURE__*/function (RecurlyCreditCardBrand) {
  RecurlyCreditCardBrand["UNKNOWN"] = "unknown";
  RecurlyCreditCardBrand["VISA"] = "visa";
  RecurlyCreditCardBrand["MASTERCARD"] = "master";
  RecurlyCreditCardBrand["AMERICANEXPRESS"] = "american_express";
  RecurlyCreditCardBrand["DISCOVER"] = "discover";
  return RecurlyCreditCardBrand;
}({});
var RecurlyCreditCardBrandMap = _defineProperty(_defineProperty(_defineProperty(_defineProperty({}, CardTypes.VISA, RecurlyCreditCardBrand.VISA), CardTypes.MASTERCARD, RecurlyCreditCardBrand.MASTERCARD), CardTypes.AMERICANEXPRESS, RecurlyCreditCardBrand.AMERICANEXPRESS), CardTypes.DISCOVER, RecurlyCreditCardBrand.DISCOVER);
var getFieldError = function getFieldError(key, _ref, rioNamespace) {
  var empty = _ref.empty,
    valid = _ref.valid;
  if (empty) {
    sendRioEvent({
      info: parseString(getString('APP.RIO.EVENTS.VALUE.INVALID_FIELD'), {
        fieldName: key
      }),
      specType: SpecType.NOTIFICATION,
      subType: SubType.ERROR,
      callback: sendRioEventCallback,
      namespace: rioNamespace,
      message_detail: InlineErrors.REQUIRED
    });
    return ValidationManager.getMessage(VALIDATION_TYPES.REQUIRED, key);
  }
  if (!valid) {
    sendRioEvent({
      info: parseString(getString('APP.RIO.EVENTS.VALUE.INVALID_FIELD'), {
        fieldName: key
      }),
      specType: SpecType.NOTIFICATION,
      subType: SubType.ERROR,
      callback: sendRioEventCallback,
      namespace: rioNamespace,
      message_detail: InlineErrors.INVALID
    });
    return ValidationManager.getMessage(VALIDATION_TYPES.INVALID, key);
  }
  return '';
};
export var getMatchedCards = function getMatchedCards(_ref2) {
  var brand = _ref2.brand;
  return Object.entries(RecurlyCreditCardBrandMap).reduce(function (acc, _ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
      key = _ref4[0],
      value = _ref4[1];
    return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, key, brand === RecurlyCreditCardBrand.UNKNOWN || brand === value));
  }, {});
};
export var focusField = function focusField(key, rioNamespace) {
  sendRioEvent({
    info: parseString(getString("APP.RIO.EVENTS.EL_NAME.USER_ENTERS_FIELD_ADD"), {
      fieldType: key
    }),
    specType: SpecType.INTERACTION,
    subType: SubType.TEXT,
    elementType: ElementType.TEXT,
    callback: sendRioEventCallback,
    namespace: rioNamespace
  });
  return updateFormFieldData(_defineProperty({}, key, {
    focused: true,
    error: ''
  }));
};
var blurField = function blurField(key, state, rioNamespace) {
  return updateFormFieldData(_defineProperty({}, key, {
    focused: false,
    empty: state.empty,
    error: getFieldError(key, state, rioNamespace)
  }));
};
export var updateMatchedCards = function updateMatchedCards(state) {
  return updatePaymentState({
    matchedCards: getMatchedCards(state)
  });
};
export var getBillingAddress = function getBillingAddress(_ref5) {
  var country = _ref5.country,
    zip = _ref5.zip,
    isZipMandatory = _ref5.isZipMandatory,
    city = _ref5.city,
    state = _ref5.state;
  return {
    country: country,
    postalCode: isZipMandatory ? zip : country !== 'GB' ? Zipcode.ABCDE : Zipcode.GB,
    city: city || EMPTY_ADDRESS_VALUE,
    state: state || EMPTY_ADDRESS_VALUE,
    addressLine1: EMPTY_ADDRESS_VALUE,
    addressLine2: EMPTY_ADDRESS_VALUE,
    phone: EMPTY_ADDRESS_VALUE
  };
};
export var useCreditCardForm = function useCreditCardForm() {
  var recurly = useRecurly();
  var formRef = useRef(null);
  var dispatch = useDispatch();
  var _useCreateRecurlyMop = useCreateRecurlyMop({
      token: '',
      isPrimary: true
    }),
    createRecurlyMopForTokenId = _useCreateRecurlyMop.createRecurlyMopForTokenId;
  var _useSelector = useSelector(configSelector),
    _useSelector$ui = _useSelector.ui,
    _useSelector$ui2 = _useSelector$ui === void 0 ? {} : _useSelector$ui,
    showSubmitButton = _useSelector$ui2.showSubmitButton,
    isNewMopAddingToMopList = _useSelector.isNewMopAddingToMopList,
    rioNamespace = _useSelector.rioNamespace,
    country = _useSelector.country;
  var _useSelector2 = useSelector(mopOnFileSelector),
    paymentMethods = _useSelector2.paymentMethods;
  var _useAppSelector = useAppSelector(paymentSelector),
    _useAppSelector$billi = _useAppSelector.billingAddress,
    city = _useAppSelector$billi.city,
    state = _useAppSelector$billi.state,
    zip = _useAppSelector$billi.zip,
    isZipMandatory = _useAppSelector$billi.isZipMandatory,
    formFields = _useAppSelector.formFields;
  var billingAddress = getBillingAddress({
    country: country,
    zip: zip,
    isZipMandatory: isZipMandatory,
    city: city,
    state: state
  });
  var hasMops = !!(paymentMethods.length > 0);
  var handleTokenizationError = function handleTokenizationError(error) {
    var _error$fields, _error$fields2;
    sendNewRelicMessage('Recurly Tokenization Failure', {
      errors: JSON.stringify(error),
      vendorType: VendorType.RECURLY
    });
    if ((_error$fields = error.fields) !== null && _error$fields !== void 0 && _error$fields.includes('first_name') || (_error$fields2 = error.fields) !== null && _error$fields2 !== void 0 && _error$fields2.includes('last_name')) {
      dispatch(updateFormFieldData(_defineProperty({}, CCFIELDS.CARDHOLDER_NAME, {
        error: error.message,
        focus: false,
        value: formFields[CCFIELDS.CARDHOLDER_NAME].value
      })));
    }
    doFireStatusUpdate([{
      status: StatusTypes.ERROR,
      command: PaymentMethodsCommandNames.FETCH_PAYMENT_METHOD,
      payload: {
        component: Component.RECURLY_SERVICE,
        errors: [ERRORS.RECURLY_TOKENIZATION_FAILURE]
      }
    }]);
    if (hasMops || showSubmitButton) {
      doFireStatusUpdate([{
        status: StatusTypes.ERROR,
        command: PaymentMethodsCommandNames.FETCH_PAYMENT_METHOD_DISABLED,
        payload: {
          component: Component.RECURLY_SERVICE,
          errors: [ERRORS.RECURLY_TOKENIZATION_FAILURE]
        }
      }]);
    }
  };
  var handleTokenizationSuccess = /*#__PURE__*/function () {
    var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(tokenId, isPrimary) {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (!formFields.userAddress.error) {
              _context.next = 6;
              break;
            }
            dispatch(updateFormFieldData(_defineProperty({}, CCFIELDS.BILLING_ZIP, {
              error: formFields.userAddress.error,
              focus: false,
              value: billingAddress.postalCode
            })));
            // Let host know that there were inline errors
            doFireStatusUpdate([{
              status: StatusTypes.ERROR,
              command: PaymentMethodsCommandNames.FETCH_PAYMENT_METHOD,
              payload: {
                component: Component.RECURLY_SERVICE,
                errors: [ERRORS.INLINE_ERRORS]
              }
            }]);
            // Keep the Join Now CTA disabled in case of multiple MOPs and appMode is ADD/EDIT
            if (hasMops || showSubmitButton) {
              doFireStatusUpdate([{
                status: StatusTypes.ERROR,
                command: PaymentMethodsCommandNames.FETCH_PAYMENT_METHOD_DISABLED,
                payload: {
                  component: Component.RECURLY_SERVICE,
                  errors: [ERRORS.INLINE_ERRORS]
                }
              }]);
            }
            _context.next = 12;
            break;
          case 6:
            if (!isNewMopAddingToMopList) {
              _context.next = 11;
              break;
            }
            _context.next = 9;
            return createRecurlyMopForTokenId(tokenId, isPrimary);
          case 9:
            _context.next = 12;
            break;
          case 11:
            doFireStatusUpdate([{
              status: StatusTypes.SUCCESS,
              command: PaymentMethodsCommandNames.FETCH_PAYMENT_METHOD,
              payload: {
                component: Component.RECURLY_SERVICE,
                data: {
                  tokenId: tokenId,
                  billingAddress: billingAddress
                }
              }
            }]);
          case 12:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function handleTokenizationSuccess(_x, _x2) {
      return _ref6.apply(this, arguments);
    };
  }();
  var tokenizePayment = function tokenizePayment(isPrimary) {
    var form = formRef.current;
    var countryInput = form.querySelector('[data-recurly="country"]');
    countryInput.value = countryInput.dataset.recurlyCountryCode || countryInput.value;
    recurly.token(form, function (err, token) {
      if (err) {
        handleTokenizationError(err);
      } else {
        handleTokenizationSuccess(token.id, isPrimary);
        // Once the new MOP is added to the list of existing MOPs, update isNewMopAddingToMopList to false
        dispatch(saveConfigs({
          isNewMopAddingToMopList: false
        }));
      }
    });
  };
  var handleSubmit = function handleSubmit(isPrimary) {
    var _formRef$current;
    var isZipEmpty = ((_formRef$current = formRef.current) === null || _formRef$current === void 0 || (_formRef$current = _formRef$current.zip) === null || _formRef$current === void 0 ? void 0 : _formRef$current.value) === '';
    if (isZipMandatory && isZipEmpty) {
      dispatch(saveUserAddressCommands([{
        commandName: UserAddressCommandNames.VERIFY_ADDRESS
      }]));
      doFireStatusUpdate([{
        status: StatusTypes.ERROR,
        command: PaymentMethodsCommandNames.FETCH_PAYMENT_METHOD,
        payload: {
          component: Component.RECURLY_SERVICE,
          errors: [ERRORS.INLINE_ERRORS]
        }
      }]);
      // Keep the Join Now CTA disabled in case of multiple MOPs and appMode is ADD/EDIT
      if (hasMops || showSubmitButton) {
        doFireStatusUpdate([{
          status: StatusTypes.ERROR,
          command: PaymentMethodsCommandNames.FETCH_PAYMENT_METHOD_DISABLED,
          payload: {
            component: Component.RECURLY_SERVICE,
            errors: [ERRORS.INLINE_ERRORS]
          }
        }]);
      }
    } else {
      tokenizePayment(isPrimary);
    }
    dispatch(saveConfigs({
      submit: false
    }));
    dispatch(updatePaymentState({
      isLoading: false
    }));
  };
  var getChangeHandler = function getChangeHandler(key) {
    return function (state) {
      /* istanbul ignore next */
      if (key === CCFIELDS.CARD_NUMBER) {
        dispatch(updateMatchedCards(state));
      }
    };
  };
  var getBlurHandler = function getBlurHandler(key) {
    return function (_ref7) {
      var state = _ref7.state;
      dispatch(blurField(key, state, rioNamespace));
    };
  };
  var getFocusHandler = function getFocusHandler(key) {
    return function () {
      dispatch(focusField(key));
    };
  };
  var getElementEventListeners = function getElementEventListeners(key) {
    return {
      onBlur: getBlurHandler(key),
      // Recurly incorrectly types this blur prop as a VoidFunction
      onFocus: getFocusHandler(key),
      onChange: getChangeHandler(key)
    };
  };
  return {
    formRef: formRef,
    handleSubmit: handleSubmit,
    getElementEventListeners: getElementEventListeners
  };
};


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg/writing-checkout/node_modules/@chegg/payment-methods/dist/esm/business/processors/recurly/CreditCardService.js
// module id = 4620
// module chunks = 11